/**
 * Reusable RBAC permission matrix (resource × action) over
 * `/users/groups/known-permissions` — the single source for the vocabulary.
 *
 * Value/onChange speak canonical `resource:action` strings. Mirrors the group
 * picker's rules: standard CRUD actions line up on fixed columns, and ticking a
 * NON-read action auto-grants (and locks) `read` — you can't act on what you can't
 * see (the backend action hierarchy). An optional `isAvailable` predicate greys out
 * grants the caller may NOT hold (the ⊆-own-grants case for a personal token) —
 * pass a wildcard-aware check so an admin holding `*:*` isn't greyed out.
 */
import { useQuery } from "@tanstack/react-query";
import * as React from "react";

import { usersApi } from "@/lib/api-users";

// Fixed leading columns so every resource lines up (mirrors GroupDialog).
const STD_ACTIONS = ["read", "write", "delete", "execute"];

function orderActions(actions: string[]): string[] {
  const std = STD_ACTIONS.filter((a) => actions.includes(a));
  const extra = actions.filter((a) => !STD_ACTIONS.includes(a)).sort();
  return [...std, ...extra];
}

export function PermissionPicker({
  value,
  onChange,
  isAvailable,
  disabled,
}: {
  value: string[];
  onChange: (next: string[]) => void;
  /**
   * When set, a `(resource, action)` is selectable only if this predicate
   * returns true — the ⊆-own-grants case for a personal token. Pass a
   * wildcard-aware check (e.g. `hasPermission(ownGrants, r, a)`) so an admin
   * holding `*:*` isn't greyed out by a literal string mismatch.
   */
  isAvailable?: (resource: string, action: string) => boolean;
  disabled?: boolean;
}) {
  const known = useQuery({ queryKey: ["known-permissions"], queryFn: () => usersApi.knownPermissions() });
  const set = React.useMemo(() => new Set(value), [value]);

  const groups = React.useMemo(() => {
    const byGroup = new Map<string, { resource: string; actions: string[] }[]>();
    for (const r of known.data?.resources ?? []) {
      const g = byGroup.get(r.group) ?? byGroup.set(r.group, []).get(r.group)!;
      g.push({ resource: r.resource, actions: orderActions(r.actions) });
    }
    return [...byGroup.entries()];
  }, [known.data]);

  function allowed(resource: string, action: string): boolean {
    return !isAvailable || isAvailable(resource, action);
  }

  function toggle(resource: string, action: string) {
    const key = `${resource}:${action}`;
    const next = new Set(set);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
      // Non-read action implies read — auto-grant it (if the caller may hold it).
      if (action !== "read" && allowed(resource, "read")) next.add(`${resource}:read`);
    }
    onChange([...next]);
  }

  // `read` is LOCKED (auto-granted) while any non-read action on the resource is set.
  function readLocked(resource: string, actions: string[]): boolean {
    return actions.some((a) => a !== "read" && set.has(`${resource}:${a}`));
  }

  if (known.isLoading) return <div className="text-xs text-muted-foreground">…</div>;

  return (
    <div className="max-h-[22rem] space-y-3 overflow-y-auto rounded-md border border-border p-2">
      {groups.map(([group, rows]) => (
        <div key={group}>
          <div className="mb-1 px-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
            {group}
          </div>
          <table className="w-full text-left text-xs">
            <tbody>
              {rows.map(({ resource, actions }) => (
                <tr key={resource} className="border-t border-border/40">
                  <td className="py-1 pr-3 font-mono">{resource}</td>
                  {actions.map((action) => {
                    const key = `${resource}:${action}`;
                    const checked = set.has(key);
                    const lock = action === "read" && readLocked(resource, actions);
                    const dis = disabled || lock || !allowed(resource, action);
                    return (
                      <td key={action} className="py-1 pr-3">
                        <label
                          className={`flex items-center gap-1 ${dis && !checked ? "opacity-40" : ""}`}
                          title={!allowed(resource, action) ? "non hai questo permesso" : undefined}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={dis}
                            onChange={() => toggle(resource, action)}
                          />
                          <span>{action}</span>
                        </label>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}
